import React, { FC, useEffect, useState } from "react";

import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Paper, Typography, styled, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import NewOrganizationModal from "@/components/Admin/AdminDashboard/components/NewOrganisationModal";
import { tokens } from "@/components/Application/theme";

import { useFetchApplicationStatus } from "@/service/ApplicationQueries";
import { useFetchSystemSettings } from "@/service/Settings/SettingsQueries";

const StyledPaper = styled(Paper)(({ theme }) => ({
	borderLeft: `6px solid ${theme.palette.error.main}`,
	background: theme.palette.background.paper,
	boxShadow: theme.shadows[6],
	padding: theme.spacing(4, 3),
	maxWidth: 480,
	width: "100%",
	textAlign: "center",
	borderRadius: theme.shape.borderRadius * 2,
}));

export const UserNotActive: FC = () => {
	const theme = useTheme();
	const colors = tokens(theme.palette.mode);

	const [openNewOrgModal, setOpenNewOrgModal] = useState(false);
	const { isLoading, isError, data: status } = useFetchApplicationStatus();
	const { data: systemSettings, isLoading: isLoadingSystemSettings } =
		useFetchSystemSettings();

	// Open the "New Organization" modal if flagged
	useEffect(() => {
		if (status?.IsNewOrganization) {
			setOpenNewOrgModal(true);
		}
	}, [status]);

	if (isLoading || isLoadingSystemSettings) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="100vh"
			>
				<CircularProgress />
			</Box>
		);
	}

	if (isError || !status) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				minHeight="100vh"
			>
				<Typography color="error">
					Failed to load application status. Please try again later.
				</Typography>
			</Box>
		);
	}

	return (
		<Box
			component="main"
			sx={{
				minHeight: "100vh",
				width: "100%",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				bgcolor: "grey.100",
				p: 2,
			}}
		>
			<StyledPaper>
				<Box
					sx={{
						display: "inline-flex",
						alignItems: "center",
						mb: 2,
						color: "error.main",
					}}
				>
					<ErrorOutlineIcon fontSize="large" aria-hidden sx={{ mr: 1 }} />
					<Typography variant="h5" component="h1">
						Access Required
					</Typography>
				</Box>
				<Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
					You don’t currently have access to <strong>Satos</strong>. Please
					contact your administrator to request access.
				</Typography>
				{systemSettings?.UserAccessAdministrator ? (
					<>
						<Button
							variant="contained"
							component="a"
							href="api/auth/logout"
							sx={{
								mr: 2,
								px: 3,
								py: 1,
								fontWeight: 500,
								color: "white",
								backgroundColor: colors.grey[700],
								"&:hover": {
									backgroundColor: colors.grey[500],
								},
							}}
						>
							Logout
						</Button>
						<Button
							variant="contained"
							component="a"
							href={`mailto:${systemSettings.UserAccessAdministrator}`}
							sx={{ px: 3, py: 1, fontWeight: 500 }}
						>
							Contact Admin
						</Button>
					</>
				) : (
					<Button
						variant="contained"
						component="a"
						href="api/auth/logout"
						sx={{ px: 3, py: 1, fontWeight: 500 }}
					>
						Logout
					</Button>
				)}
				<NewOrganizationModal
					open={openNewOrgModal}
					handleClose={() => setOpenNewOrgModal(false)}
				/>
			</StyledPaper>
		</Box>
	);
};
